import { useNavigate } from "react-router";
import * as Sentry from "@sentry/react";
import TechnicalErrorBoundary from "./TechnicalErrorBoundary";
import NoDataDpContainer from "../NoDataDp/NoDataDp";
import styles from "./ErrorBoundary.module.scss";

interface Props {
  children: any;
  fallbackContainerClassName?: any;
}

/**
 * Sentry-enhanced Error Boundary specifically for Dashboard
 * This will capture errors with full context and enable session replay
 * In production, uses Sentry's ErrorBoundary for enhanced error tracking
 * In development, falls back to TechnicalErrorBoundary
 */
function DashboardErrorBoundary({
  children,
  fallbackContainerClassName,
}: Props) {
  const navigate = useNavigate();

  const fallback = (
    <div
      className={
        fallbackContainerClassName ?? styles.technicalErrorBoundary
      }
    >
      <NoDataDpContainer
        title={"Technical Error"}
        subTitle={
          <>
            {`This is due to a technical error with Merito!`}
            <br />
            You can send a support message below or call +919867054499
          </>
        }
        button
        buttonText={"Go to Support"}
        onClick={() => navigate("/support")}
      />
    </div>
  );

  // Use Sentry's ErrorBoundary in production or when local testing is enabled
  const isProduction = import.meta.env.MODE === "production" || import.meta.env.PROD;
  const enableLocal = import.meta.env.VITE_ENABLE_SENTRY_LOCAL === "true";

  if (isProduction || enableLocal) {
    return (
      <Sentry.ErrorBoundary
        fallback={fallback}
        showDialog={false}
        beforeCapture={(scope, error) => {
          // Add dashboard-specific context
          scope.setTag("route", "dashboard");
          scope.setTag("component", "Dashboard");
          scope.setContext("dashboard", {
            url: window.location.href,
            pathname: window.location.pathname,
            timestamp: new Date().toISOString(),
          });
        }}
      >
        {children}
      </Sentry.ErrorBoundary>
    );
  }

  // In development, use regular TechnicalErrorBoundary
  return (
    <TechnicalErrorBoundary fallbackContainerClassName={fallbackContainerClassName}>
      {children}
    </TechnicalErrorBoundary>
  );
}

export default DashboardErrorBoundary;

